/// <amd-module name="Search/WorkItem/Components/ZeroData" />
import * as React from "react";
import * as Container from "Search/Shared/Base/Container";
import * as ZeroData from "Search/Shared/Components/ZeroData";
import * as Constants from "Search/WorkItem/Constants";
import * as SharedResources from "Search/Shared/Resources";
import * as WorkItemResources from "Search/WorkItem/Resources";
import * as Utils_Accessibility from 'VSS/Core/Util/Accessibility';
import { format } from "VSS/Core/Util/String";
import { IVssPageService } from "VSS/Platform/Page";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { ScenarioType } from "Search/Shared/Components/ZeroData/ZeroData.Props";
import { ActionCreator } from "Search/WorkItem/Flux/ActionCreator";
import { StoresHub, IAggregatedState, StoreName } from "Search/WorkItem/Flux/StoresHub"

export const ZeroDataContainer = Container.create<StoreName, ActionCreator, IAggregatedState, StoresHub>(
    [
        "zeroDataStore"
    ],
    ({
        searchStoreState,
        zeroDataState
    }, props) => {
        const { scenario } = zeroDataState;
        if (scenario && scenario !== ZeroData.ScenarioType.None) {
            const { activityId, query, error } = searchStoreState;
            const {
                notifyResultsRendered,
                publishZeroData,
                onPreviewLoaded,
                notifySearchFailed,
                notifyFeedbackMailLinkClicked
            } = props.actionCreator
            const pageService: IVssPageService = props.pageContext.getService<IVssPageService>("IVssPageService");
            const isHosted = pageService.getData()!.isHosted;
            let zeroDataProps = {} as ZeroData.IZeroDataProps;

            if (scenario === ZeroData.ScenarioType.LandingPage) {
                zeroDataProps.message = WorkItemResources.WorkItemWelcomeText;
                zeroDataProps.help = "";
                zeroDataProps.announceMessage = WorkItemResources.WorkItemWelcomeText;
            }
            else {
                zeroDataProps = getZeroDataProps(
                    query.searchText,
                    activityId,
                    scenario,
                    notifyFeedbackMailLinkClicked,
                    isHosted);
            }

            return <ZeroData.ZeroData
                {...zeroDataProps}
                pageContext={props.pageContext}
                onDidMount={
                    () => {
                        Utils_Accessibility.announce(zeroDataProps.announceMessage, true);
                        publishZeroData(ZeroData.ScenarioType[scenario], error);
                        onDidMount(scenario, notifyResultsRendered, notifySearchFailed, onPreviewLoaded)
                    }
                } />;
        }

        return null;
    });

function getZeroDataProps(
    _queryText: string,
    _activityId: string,
    _scenario: ScenarioType,
    _notifyFeedbackMailLinkClicked: () => void,
    _isHosted: boolean
): ZeroData.IZeroDataProps {
    let zeroDataProps = {} as ZeroData.IZeroDataProps;

    if (_scenario === ZeroData.ScenarioType.ServiceError) {
        const messageText: string = SharedResources.ServiceErrorMessage;
        const feedbackLink = _isHosted
            ? format(Constants.DevOpsFeedbackMailToLinkFormat, _activityId)
            : format(Constants.FeedbackMailToLinkFormat, _activityId);
        zeroDataProps.scenario = ZeroData.ScenarioType.ServiceError;
        zeroDataProps.message = messageText;
        zeroDataProps.help = ZeroData.getServiceErrorHelp(_isHosted, feedbackLink);
        zeroDataProps.announceMessage = SharedResources.ServiceErrorMessage;
    } else {
        zeroDataProps.scenario = _scenario;
        if (zeroDataProps.scenario === ZeroData.ScenarioType.ZeroResults) {
            const feedbackLink = _isHosted
                ? format(Constants.DevOpsFeedbackMailToLinkFormat, _activityId)
                : format(Constants.FeedbackMailToLinkFormat, _activityId);
            const learnMoreLinkAriaLabel = format(SharedResources.LearnMoreAriaLabel, WorkItemResources.WorkItemSearchAriaLabel);
            const message: JSX.Element =
                <FormatComponent format={WorkItemResources.ZeroWorkItemResultsMessageFormat}>
                    {
                        <span className="searchText">
                            {_queryText}
                        </span>
                    }
                </FormatComponent>;

            const help: JSX.Element = ZeroData.getZeroResultsHelpComponent(
                _isHosted,
                SharedResources.ZeroResultsHelpText,
                WorkItemResources.ZeroWorkItemResultsLearnMoreFormat,
                Constants.LearnMoreLink,
                learnMoreLinkAriaLabel,
                feedbackLink,
                _notifyFeedbackMailLinkClicked);

            zeroDataProps.message = message;
            zeroDataProps.help = help;
            zeroDataProps.announceMessage = format(WorkItemResources.ZeroWorkItemResultsMessageFormat, _queryText);
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.PrefixWildCardNotSupported) {
            zeroDataProps.message = ZeroData.getQueryNotSupportedHelp(_queryText, SharedResources.WildCardNotSupportedHelpFormat);
            zeroDataProps.help = SharedResources.WildCardNotSupportedMessage;
            zeroDataProps.announceMessage = format(SharedResources.WildCardNotSupportedHelpFormat, _queryText)
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.NoPermission) {
            zeroDataProps.message = WorkItemResources.ZeroWorkItemResultsNoPermissionMessage;
            zeroDataProps.help = ZeroData.getAccessPermissionHelp();
            zeroDataProps.announceMessage = WorkItemResources.ZeroWorkItemResultsNoPermissionMessage;
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.IndexingScenario) {
            const feedbackLink = _isHosted
                ? format(Constants.DevOpsFeedbackMailToLinkFormat, _activityId)
                : format(Constants.FeedbackMailToLinkFormat, _activityId);
            zeroDataProps.message = SharedResources.AccountIndexingMessage;
            zeroDataProps.help = ZeroData.getIndexingHelp(_isHosted, feedbackLink);
            zeroDataProps.announceMessage = SharedResources.AccountIndexingMessage;
        }
    }

    return zeroDataProps;
}

function onDidMount(
    _zeroDataScenario: ZeroData.ScenarioType,
    _notifyResultsRendered: () => void,
    _notifySearchFailed: () => void,
    _onPreviewLoaded: () => void): void {
    const searchFailed = _zeroDataScenario === ZeroData.ScenarioType.ServiceError;

    if (!searchFailed) {
        _notifyResultsRendered();
        _onPreviewLoaded();
    }
    else {
        _notifySearchFailed();
    }
}
